import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
    endpoints,
    type ChangePasswordBody,
    type RequestEmailChangeBody,
    type TwoFactorConfirmBody,
    type TwoFactorDisableBody,
    type UpdateCompanyBody,
    type UpdateProfileBody,
} from './api';
import { SESSION_KEY, usePermittedMutation } from './session';

/**
 * A person's own account.
 *
 * Everything here invalidates the session query, because the name and the
 * address it holds are what the panel shows back to them: a save that does
 * not refresh those looks like a save that did not happen.
 */

export function useUpdateProfile() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (body: UpdateProfileBody) => endpoints.updateProfile(body),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: SESSION_KEY }),
    });
}

/** The company's own record. Nothing could write these columns before. */
export function useCompany() {
    return useQuery({ queryKey: ['company'], queryFn: endpoints.company });
}

export function useUpdateCompany() {
    const queryClient = useQueryClient();
    return usePermittedMutation('tenant.write', {
        mutationFn: (body: UpdateCompanyBody) => endpoints.updateCompany(body),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['company'] });
            void queryClient.invalidateQueries({ queryKey: SESSION_KEY });
            // Whether a purchase may go ahead is read from these details, and
            // the seller comes back from here to the screen that asked for them.
            void queryClient.invalidateQueries({ queryKey: ['payment-options'] });
        },
    });
}

export function useChangePassword() {
    return useMutation({
        mutationFn: (body: ChangePasswordBody) => endpoints.changePassword(body),
    });
}

/**
 * Two-step sign-in (docs/07 §4): a new authenticator secret, then the first
 * code that turns it on and returns the recovery codes, shown once. Session
 * writes, so plain mutations; on and off both change what the session says.
 */
export function useBeginTwoFactor() {
    return useMutation({ mutationFn: () => endpoints.beginTwoFactor() });
}

export function useConfirmTwoFactor() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (body: TwoFactorConfirmBody) => endpoints.confirmTwoFactor(body),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: SESSION_KEY }),
    });
}

export function useDisableTwoFactor() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (body: TwoFactorDisableBody) => endpoints.disableTwoFactor(body),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: SESSION_KEY }),
    });
}

export function useRequestEmailChange() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (body: RequestEmailChangeBody) => endpoints.requestEmailChange(body),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: SESSION_KEY }),
    });
}

export function useForgotPassword() {
    return useMutation({
        mutationFn: (email: string) => endpoints.forgotPassword(email),
    });
}

export function useResetPassword() {
    return useMutation({
        mutationFn: ({ token, password }: { token: string; password: string }) => endpoints.resetPassword(token, password),
    });
}

/** The verification link again, for an address that is still unconfirmed. */
export function useResendEmailVerification() {
    return useMutation({
        mutationFn: () => endpoints.resendEmailVerification(),
    });
}

/** Where this account is signed in, so a stray session can be ended. */
export function useSessions() {
    return useQuery({ queryKey: ['sessions'], queryFn: endpoints.sessions });
}

export function useRevokeSession() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id: string) => endpoints.revokeSession(id),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sessions'] }),
    });
}

/** Sign out everywhere but here. */
export function useEndOtherSessions() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () => endpoints.endOtherSessions(),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sessions'] }),
    });
}

/**
 * A session's device in words a person recognises — "Chrome · macOS" — rather
 * than the user-agent string it was read from (docs/15 §6). Null when the string
 * names nothing known, so the screen says "Bilinmeyen cihaz" instead of guessing.
 *
 * Order matters: an iPhone also claims "Mac OS X", Android and ChromeOS also
 * claim "Linux", and every Chromium browser also claims "Chrome" and "Safari".
 */
export function describeDevice(userAgent: string): string | null {
    const browser = /Edg(A|iOS)?\//.test(userAgent)
        ? 'Edge'
        : /OPR\//.test(userAgent)
          ? 'Opera'
          : /SamsungBrowser\//.test(userAgent)
            ? 'Samsung Internet'
            : /Firefox\/|FxiOS\//.test(userAgent)
              ? 'Firefox'
              : /Chrome\/|CriOS\//.test(userAgent)
                ? 'Chrome'
                : /Version\/.*Safari\//.test(userAgent)
                  ? 'Safari'
                  : null;
    const system = /iPhone|iPod/.test(userAgent)
        ? 'iOS'
        : /iPad/.test(userAgent)
          ? 'iPadOS'
          : /Android/.test(userAgent)
            ? 'Android'
            : /Windows/.test(userAgent)
              ? 'Windows'
              : /CrOS/.test(userAgent)
                ? 'ChromeOS'
                : /Macintosh|Mac OS X/.test(userAgent)
                  ? 'macOS'
                  : /Linux/.test(userAgent)
                    ? 'Linux'
                    : null;
    const parts = [browser, system].filter((part): part is string => part !== null);
    return parts.length > 0 ? parts.join(' · ') : null;
}
